// «Диалоги» (NPC visits) and «Чаты» (player DiscussionRooms) sub-tabs of the
// Действия screen. Isolated .gn-* markup; logic reused from cycle/NegotiationRooms
// (useAssetContent, npcVisitTerminalInfo, roomTitleForList) + cycle/shared, all
// mutations via the shared action() wrapper (re-fetches setup → messages refresh).

import { useMemo, useState, type KeyboardEvent } from "react";
import { api, type CampaignSetup, type DiscussionRoom, type NpcVisit } from "../api";
import { npcVisitTerminalInfo, npcVisitIsOpen, roomTitleForList, useAssetContent } from "../cycle/NegotiationRooms";
import { formatTime, initials, previewText } from "../cycle/shared";

type RunAction = (label: string, run: () => Promise<unknown>) => Promise<boolean>;
const GEN = "/assets/game-ui/gen";

function minutesLeft(iso?: string): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.round(ms / 60000);
}
function lastMessagePreview(room: DiscussionRoom): string {
  const last = room.messages[room.messages.length - 1];
  if (!last) return "Сообщений пока нет";
  const who = last.isMine ? "вы" : last.senderDisplayName;
  return `${who}: ${previewText(last.content || last.actionText || "", 60)}`;
}

function NpcAvatar({ campaignId, visit, hero }: { campaignId: string; visit: NpcVisit; hero?: boolean }) {
  const src = useAssetContent(campaignId, visit.portraitImages?.[0]);
  return (
    <span className={hero ? "gn-nmed" : "gn-rmed"}>
      {src ? <img src={src} alt="" /> : <>{initials(visit.characterName)}</>}
    </span>
  );
}

function GnRoom({
  campaignId,
  room,
  visit,
  setup,
  action,
}: {
  campaignId: string;
  room: DiscussionRoom;
  visit?: NpcVisit;
  setup: CampaignSetup;
  action: RunAction;
}) {
  const [msg, setMsg] = useState("");
  const isNpc = !!visit;
  const closed = !!room.closedAt || (visit ? !npcVisitIsOpen(visit, room) : !room.canPost);

  async function send() {
    const text = msg.trim();
    if (!text) return;
    setMsg(""); // очищаем поле сразу (оптимистично), не дожидаясь раунд-трипа
    const ok = await action("Отправляем сообщение…", () =>
      api.sendDiscussionMessage(campaignId, room.id, text),
    );
    if (!ok) setMsg(text); // вернуть текст, если отправка не удалась
  }
  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      void send();
    }
  }

  const terminal = visit && closed ? npcVisitTerminalInfo(visit) : null;

  return (
    <div className="gn-card">
      {isNpc ? (
        <div className="gn-nhero">
          <NpcAvatar campaignId={campaignId} visit={visit} hero />
          <div className="gn-nm">
            <span>{closed ? "Завершённая встреча" : "Персонаж встречи"}</span>
            <b>{visit.characterName}</b>
            <i>{visit.characterTitle}</i>
          </div>
        </div>
      ) : (
        <div className="gn-nm">
          <span>{room.roomType === "PUBLIC" ? "Общая комната" : "Закрытая комната"}</span>
          <b>{roomTitleForList(room)}</b>
          <i>{room.members.length} участников</i>
        </div>
      )}

      {isNpc && !closed && (
        <div className="gn-stagerow">
          <span>
            Стадия: <b>{visit.dialogueStageLabel.toLowerCase()}</b>
          </span>
          {minutesLeft(visit.expiresAt) != null && <span>⏳ {minutesLeft(visit.expiresAt)} мин</span>}
        </div>
      )}

      {isNpc && visit.offerGivesText && (
        <div className="gn-offer">
          <div className="gn-o">
            <span className="gn-ok">Что предлагает</span>
            <span className="gn-ov">{visit.offerGivesText}</span>
          </div>
          {visit.offerRequestsText && (
            <div className="gn-o">
              <span className="gn-ok">Что просит</span>
              <span className="gn-ov">{visit.offerRequestsText}</span>
            </div>
          )}
          {visit.offerDurationText && (
            <div className="gn-o">
              <span className="gn-ok">Срок</span>
              <span className="gn-ov">{visit.offerDurationText}</span>
            </div>
          )}
        </div>
      )}

      {terminal && (
        <div className={`gn-nterminal ${terminal.tone}`}>
          <b>{terminal.title}</b>
          <p>{terminal.text}</p>
          {terminal.consequence && <small>{terminal.consequence}</small>}
        </div>
      )}

      <div className="gn-msgs">
        {room.messages.length === 0 ? (
          <div className="gn-empty2">Сообщений пока нет.</div>
        ) : (
          room.messages.map((m) => (
            <div
              key={m.id}
              className={`gn-msg ${m.senderType === "SYSTEM" ? "sys" : m.isMine ? "mine" : "them"}`}
            >
              {m.senderType !== "SYSTEM" && (
                <div className="gn-who">
                  {m.senderDisplayName} · {formatTime(m.createdAt)}
                </div>
              )}
              {m.content && <div>{m.content}</div>}
              {m.actionText && <div className="gn-narr">{m.actionText}</div>}
            </div>
          ))
        )}
      </div>

      {!isNpc && <Agreements campaignId={campaignId} room={room} action={action} />}

      {closed ? (
        <div className="gn-chint" style={{ marginTop: "12px" }}>
          {isNpc ? "Встреча закрыта" : "Комната закрыта"} — история сохранена.
        </div>
      ) : (
        <div className="gn-composer">
          <div className="gn-crow">
            <input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={onKey}
              placeholder={
                isNpc
                  ? "Задайте вопрос или предложите изменение условий"
                  : "Напишите сообщение участникам комнаты"
              }
            />
            <button type="button" className="gn-snd" disabled={!msg.trim()} onClick={() => void send()}>
              ➤
            </button>
          </div>
          {isNpc && (
            <div className="gn-chint">
              Исход определит разговор — согласие, отказ или условия сформулируйте словами.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Agreements({
  campaignId,
  room,
  action,
}: {
  campaignId: string;
  room: DiscussionRoom;
  action: RunAction;
}) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [terms, setTerms] = useState("");

  async function save() {
    if (title.trim().length < 5 || terms.trim().length < 20) return;
    const ok = await action("Фиксируем договорённость…", () =>
      api.createNegotiationAgreement(campaignId, room.id, {
        title: title.trim(),
        terms: terms.trim(),
        participantUserIds: room.members.map((m) => m.userId),
      }),
    );
    if (ok) {
      setTitle("");
      setTerms("");
      setAdding(false);
    }
  }

  return (
    <div className="gn-agree">
      <span className="gn-eyebrow">Зафиксированные договорённости</span>
      {room.agreements.length === 0 && !adding && (
        <div className="gn-empty2">Договорённостей пока нет.</div>
      )}
      {room.agreements.map((a) => (
        <div className="gn-acard" key={a.id}>
          <b>{a.title}</b>
          {a.linkedInitiativeId && <span className="gn-agtag"> ✔ прикреплена к инициативе</span>}
          <p>{a.terms}</p>
        </div>
      ))}
      {adding ? (
        <div style={{ marginTop: "9px" }}>
          <input
            className="gn-input"
            placeholder="Краткое название"
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            className="gn-ta"
            placeholder="Что обязуются сделать стороны"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
          />
          <div className="gn-form-actions">
            <button
              type="button"
              className="gn-cta"
              disabled={title.trim().length < 5 || terms.trim().length < 20}
              onClick={() => void save()}
            >
              Сохранить
            </button>
            <button type="button" className="gn-darkbtn" onClick={() => setAdding(false)}>
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="gn-dashbtn" onClick={() => setAdding(true)}>
          Зафиксировать договорённость <span className="gn-plus">＋</span>
        </button>
      )}
    </div>
  );
}

export function GnDialoguesTab({
  campaignId,
  setup,
  action,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: RunAction;
}) {
  const cycle = setup.cycle.current;
  const pairs = useMemo(() => {
    if (!cycle) return [] as { visit: NpcVisit; room: DiscussionRoom }[];
    return cycle.npcVisits
      .map((v) => ({ visit: v, room: cycle.rooms.find((r) => r.npcVisitId === v.id) }))
      .filter((x): x is { visit: NpcVisit; room: DiscussionRoom } => !!x.room)
      .sort((a, b) => Number(npcVisitIsOpen(b.visit, b.room)) - Number(npcVisitIsOpen(a.visit, a.room)));
  }, [cycle]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!cycle) return null;
  if (pairs.length === 0) {
    return (
      <div className="gn-body">
        <div className="gn-card gn-empty">Пока никто из NPC не пришёл на встречу.</div>
      </div>
    );
  }
  const selected = pairs.find((p) => p.visit.id === selectedId) ?? pairs[0];

  return (
    <div className="gn-body">
      <div className="gn-card">
        <span className="gn-eyebrow">Встречи дня</span>
        {pairs.map(({ visit, room }) => {
          const open = npcVisitIsOpen(visit, room);
          const mins = minutesLeft(visit.expiresAt);
          return (
            <button
              key={visit.id}
              type="button"
              className={`gn-row${visit.id === selected.visit.id ? " on" : ""}`}
              onClick={() => setSelectedId(visit.id)}
            >
              <NpcAvatar campaignId={campaignId} visit={visit} />
              <div className="gn-rm">
                <b>{visit.characterName}</b>
                <span>{visit.characterTitle}</span>
              </div>
              <div className="gn-rr">
                <div className="gn-stage">{open ? visit.dialogueStageLabel : visit.statusLabel}</div>
                {open && mins != null && <div className="gn-timer">⏳ {mins} мин</div>}
              </div>
              {open && <span className="gn-dot" />}
            </button>
          );
        })}
      </div>

      <div className="gn-sect">Разговор</div>
      <GnRoom
        key={selected.room.id}
        campaignId={campaignId}
        room={selected.room}
        visit={selected.visit}
        setup={setup}
        action={action}
      />
    </div>
  );
}

export function GnChatsTab({
  campaignId,
  setup,
  action,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: RunAction;
}) {
  const cycle = setup.cycle.current;
  const rooms = useMemo(
    () => (cycle ? cycle.rooms.filter((r) => !r.roomType.startsWith("NPC_")) : []),
    [cycle],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [members, setMembers] = useState<string[]>([]);

  if (!cycle) return null;
  const others = setup.members.filter((m) => !m.isCurrentUser);
  const selected = rooms.find((r) => r.id === selectedId) ?? rooms[0];

  async function createRoom() {
    if (title.trim().length < 3 || members.length === 0) return;
    const ok = await action("Создаём закрытую комнату…", () =>
      api.createDiscussionRoom(campaignId, title.trim(), members),
    );
    if (ok) {
      setTitle("");
      setMembers([]);
      setCreating(false);
    }
  }
  function toggleMember(id: string) {
    setMembers((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  return (
    <div className="gn-body">
      <div className="gn-card">
        <span className="gn-eyebrow">Комнаты</span>
        {rooms.map((room) => (
          <button
            key={room.id}
            type="button"
            className={`gn-row${room.id === selected?.id ? " on" : ""}`}
            onClick={() => setSelectedId(room.id)}
          >
            <span className="gn-rmed">
              <img src={`${GEN}/${room.roomType === "PUBLIC" ? "room_public" : "room_private"}.png`} alt="" />
            </span>
            <div className="gn-rm">
              <b>{roomTitleForList(room)}</b>
              <span>{lastMessagePreview(room)}</span>
            </div>
            {room.unreadCount > 0 && <span className="gn-unread">{room.unreadCount}</span>}
          </button>
        ))}

        {creating ? (
          <div style={{ marginTop: "9px" }}>
            <input
              className="gn-input"
              placeholder="Название комнаты"
              value={title}
              maxLength={80}
              onChange={(e) => setTitle(e.target.value)}
            />
            <div className="gn-coactors">
              {others.map((m) => (
                <label className="gn-coactor" key={m.userId}>
                  <input
                    type="checkbox"
                    checked={members.includes(m.userId)}
                    onChange={() => toggleMember(m.userId)}
                  />
                  <b>{m.displayName}</b>
                </label>
              ))}
            </div>
            <div className="gn-form-actions">
              <button
                type="button"
                className="gn-cta"
                disabled={title.trim().length < 3 || members.length === 0}
                onClick={() => void createRoom()}
              >
                Создать
              </button>
              <button type="button" className="gn-darkbtn" onClick={() => setCreating(false)}>
                Отмена
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="gn-dashbtn" onClick={() => setCreating(true)}>
            Создать закрытую комнату <span className="gn-plus">＋</span>
          </button>
        )}
      </div>

      {selected && (
        <>
          <div className="gn-sect">{roomTitleForList(selected)}</div>
          <GnRoom key={selected.id} campaignId={campaignId} room={selected} setup={setup} action={action} />
        </>
      )}
    </div>
  );
}
